import { useState, useEffect, ChangeEvent, KeyboardEvent, useContext } from 'react';
import {
    submitCreateAssistant,
    enableAssistant,
    resolveEntities
} from '../../../api/api';
import { AppStateContext } from '../../../state/AppProvider';
import { Assistant, AzureGroup, SearchEntity } from "../../../api/models";
import ClipLoader from "react-spinners/ClipLoader";
import { ErrorDialog } from '../../ErrorDialog';
import { FileUploadArea } from '../ui/FileUploadArea';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { useFileProcessing } from '../../../hooks/useFileProcessing';
import { FileProcessingModal } from '../ui/FileProcessingModal';
import { TitleField } from '../ui/TitleField';
import { UrlField } from '../ui/UrlField';
import { PromptField } from '../ui/PromptField';
import { WelcomeField } from '../ui/WelcomeField';
import { ToolsField } from '../ui/ToolsField';
import { UsersField } from '../ui/UsersField';
import { useAssistantsRefresh } from '../hooks/useAssistantsRefresh';
import '../ui/AssistantBuilder.css';

interface AssistantCreatorProps {
    onSubmit: () => void;
    onCancel: () => void;
}

export const AssistantCreator = ({ onSubmit, onCancel }: AssistantCreatorProps) => {
    const appStateContext = useContext(AppStateContext);

    const [title, setTitle] = useState('');
    const [promptMessage, setPromptMessage] = useState('');
    const [welcomeMessage, setWelcomeMessage] = useState('');
    const [assistantUrl, setAssistantUrl] = useState('');
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [selectedEntities, setSelectedEntities] = useState<SearchEntity[]>([]);
    const [selectedManagerEntities, setSelectedManagerEntities] = useState<SearchEntity[]>([]);
    const [currentPhase, setCurrentPhase] = useState<string>('');
    const [enabledTools, setEnabledTools] = useState<Set<string>>(new Set());
    const [toolDefaults, setToolDefaults] = useState<Set<string>>(new Set());

    const [errorMessage, setErrorMessage] = useState<string>('');
    const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
    const [createdAssistantId, setCreatedAssistantId] = useState<string | null>(null);

    const {
        uploadedFiles,
        isDragOver,
        handleDrop,
        handleDragOver,
        handleDragLeave,
        handleFileChange,
        handleRemoveFile,
        documentSizeLimitMb,
        videoSizeLimitGb,
        fileLimit
    } = useFileUpload({
        onError: (msg) => openErrorDialog(msg)
    });

    const {
        currentFileProgress,
        currentFileIndex,
        totalFiles,
        uploadDocument,
        uploadVideo,
        resetProgress
    } = useFileProcessing();

    const allAssistants = appStateContext?.state?.assistants || [];
    const existingAssistantUrls: string[] = allAssistants
        .filter((a: Assistant) => a?.id !== createdAssistantId)
        .map((a: Assistant) => (a?.url || '').toLowerCase())
        .filter(Boolean);

    const isAssistantUrlDuplicate = assistantUrl.length > 0 && existingAssistantUrls.includes(assistantUrl.toLowerCase());
    const fullUrlPreview = `https://chat.jtcgroup.ai/${assistantUrl}`;

    const openErrorDialog = (msg: string) => {
        setErrorMessage(msg);
        setShowErrorDialog(true);
    };

    const toggleTool = (tool: string) => {
        if (enabledTools.has(tool)) {
            setToolDefaults(prev => {
                const next = new Set(prev);
                next.delete(tool);
                return next;
            });
        }
        setEnabledTools(prev => {
            const next = new Set(prev);
            if (next.has(tool)) next.delete(tool);
            else next.add(tool);
            return next;
        });
    };

    const toggleDefaultTool = (tool: string) => {
        if (!enabledTools.has(tool)) return;
        setToolDefaults(prev => {
            const next = new Set(prev);
            if (next.has(tool)) next.delete(tool);
            else next.add(tool);
            return next;
        });
    };

    const handleEntitySelect = (entity: SearchEntity) => {
        setSelectedEntities((prev) => [...prev, entity]);
    };

    const handleEntityRemove = (entityId: string) => {
        setSelectedEntities((prev) => prev.filter((entity) => entity.id !== entityId));
    };

    const handleManagerSelect = (entity: SearchEntity) => {
        setSelectedManagerEntities((prev) => [...prev, entity]);
    };

    const handleManagerRemove = (entityId: string) => {
        setSelectedManagerEntities((prev) => prev.filter((entity) => entity.id !== entityId));
    };

    const builderManagerGroupId = appStateContext?.state.assistantBuilderManagerGroupId ?? null;

    // The assistant builder manager group is always a manager and cannot be removed.
    useEffect(() => {
        if (!builderManagerGroupId) return;
        const seedBuilderManagerGroup = async () => {
            const fallback: AzureGroup = {
                id: builderManagerGroupId,
                displayName: 'Assistant Builder Managers',
                objectType: 'group'
            };
            let entity: SearchEntity = fallback;
            try {
                const resolved = await resolveEntities([], [builderManagerGroupId]);
                if (resolved.length > 0) entity = resolved[0];
            } catch (err) {
                console.error('resolveEntities failed:', err);
            }
            setSelectedManagerEntities((prev) =>
                prev.some((e) => e.id === builderManagerGroupId) ? prev : [entity, ...prev]
            );
        };
        seedBuilderManagerGroup();
    }, [builderManagerGroupId]);

    const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value.slice(0, 25);
        setTitle(newValue);
    };

    const handleTitleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (title.length >= 25 && allowedKeys.indexOf(e.key) === -1 && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
        }
    };

    const handlePromptMessageChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value.slice(0, 10000);
        setPromptMessage(newValue);
    };

    const handlePromptMessageKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (promptMessage.length >= 10000 && !allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
        }
    };

    const handleWelcomeMessageChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value.slice(0, 1500);
        setWelcomeMessage(newValue);
    };

    const handleWelcomeMessageKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (welcomeMessage.length >= 1500 && !allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
        }
    };

    const handleAssistantUrlChange = (e: ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const sanitized = raw.toLowerCase().replace(/[^a-z0-9-]/g, '');
        setAssistantUrl(sanitized);
    };

    const refreshAssistantsList = useAssistantsRefresh({ clearListsOnError: true });

    const handleSubmit = async () => {
        if (isDisabled) return;
        setIsDisabled(true);

        setFormSubmitted(true);
        if (!title || !promptMessage || !assistantUrl || isAssistantUrlDuplicate) {
            setIsDisabled(false);
            return;
        }

        try {
            setIsSubmitting(true);
            setCurrentPhase('Initialising assistant');
            resetProgress();

            const selectedUserIds = selectedEntities
                .filter((entity) => entity.objectType === "user")
                .map((entity) => entity.id);
            const selectedGroupIds = selectedEntities
                .filter((entity) => entity.objectType === "group")
                .map((entity) => entity.id);
            const selectedManagerIds = selectedManagerEntities
                .filter((entity) => entity.objectType === "user")
                .map((entity) => entity.id);
            const selectedManagerGroupIds = selectedManagerEntities
                .filter((entity) => entity.objectType === "group")
                .map((entity) => entity.id);

            const { ok, data } = await submitCreateAssistant(
                title,
                assistantUrl,
                promptMessage,
                welcomeMessage,
                selectedUserIds,
                selectedGroupIds,
                Array.from(enabledTools),
                Array.from(toolDefaults),
                selectedManagerIds,
                selectedManagerGroupIds
            );

            if (!ok) {
                openErrorDialog(data?.error ?? 'Failed to create your assistant.');
                setIsDisabled(false);
                setIsSubmitting(false);
                return;
            }

            const createdAssistantId = data?.id!;
            setCreatedAssistantId(createdAssistantId);

            if (uploadedFiles.length > 0) {
                await refreshAssistantsList();

                const documentFiles = uploadedFiles
                    .filter(uf => uf.type === 'document')
                    .map(uf => uf.file);
                const videoFiles = uploadedFiles
                    .filter(uf => uf.type === 'video')
                    .map(uf => uf.file);

                const allErrors: Array<{ filename: string; error: string }> = [];

                if (documentFiles.length > 0) {
                    for (let i = 0; i < documentFiles.length; i++) {
                        const documentFile = documentFiles[i];
                        setCurrentPhase(`Processing file ${i + 1} of ${documentFiles.length + videoFiles.length}: ${documentFile.name}`);

                        const result = await uploadDocument(
                            createdAssistantId,
                            documentFile,
                            i,
                            documentFiles.length + videoFiles.length
                        );

                        if (!result.success) {
                            await enableAssistant(createdAssistantId);
                            await refreshAssistantsList();
                            allErrors.push({
                                filename: documentFile.name,
                                error: result.error || 'Unknown error'
                            });
                        }
                    }
                }

                if (videoFiles.length > 0) {
                    for (let i = 0; i < videoFiles.length; i++) {
                        const videoFile = videoFiles[i];
                        const fileIndex = documentFiles.length + i;
                        setCurrentPhase(`Processing file ${fileIndex + 1} of ${documentFiles.length + videoFiles.length}: ${videoFile.name}`);

                        const result = await uploadVideo(
                            createdAssistantId,
                            videoFile,
                            fileIndex,
                            documentFiles.length + videoFiles.length
                        );

                        if (!result.success) {
                            await enableAssistant(createdAssistantId);
                            await refreshAssistantsList();
                            allErrors.push({
                                filename: videoFile.name,
                                error: result.error || 'Unknown error'
                            });
                        }
                    }
                }

                if (allErrors.length > 0) {
                    const msg = allErrors.map(e => `${e.filename}: ${e.error}`).join('\n');
                    openErrorDialog(`Some files failed:\n${msg}`);
                    setIsDisabled(false);
                    setIsSubmitting(false);
                    return;
                }
            }

            setCurrentPhase('Finalising assistant...');
            await enableAssistant(createdAssistantId);
            await refreshAssistantsList();

            setIsSuccess(true);
            setCurrentPhase('');

        } catch (error) {
            openErrorDialog(error instanceof Error ? error.message : 'An unexpected error occurred.');
            setIsDisabled(false);
            setIsSubmitting(false);
            setCurrentPhase('');
        }
    };

    return (
        <div className="relative flex flex-col h-full min-w-0">
            <FileProcessingModal
                isVisible={isSubmitting && !isSuccess}
                currentPhase={currentPhase}
                currentFileProgress={currentFileProgress}
                currentFileIndex={currentFileIndex}
                totalFiles={totalFiles}
                isEditing={false}
            />

            <fieldset className="h-full min-w-0 w-full">
                <div className="flex flex-col h-full relative min-w-0">
                    <div
                        className={
                            isSuccess
                                ? 'flex flex-grow items-center justify-center'
                                : 'flex-grow min-w-0 overflow-y-auto overflow-x-hidden p-4'
                        }
                    >
                        {isSuccess ? (
                            <div className="flex flex-col justify-center items-center">
                                <h2 className="text-xl font-semibold text-jtc-pink mb-2">
                                    Assistant Created!
                                </h2>
                                <svg
                                    className="checkmark"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 52 52"
                                >
                                    <circle
                                        className="checkmark__circle"
                                        cx="26"
                                        cy="26"
                                        r="25"
                                        fill="none"
                                    />
                                    <path
                                        className="checkmark__check"
                                        fill="none"
                                        d="M14 27l7 7 16-16"
                                    />
                                </svg>
                            </div>
                        ) : (
                            <div className={`transition-opacity ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}>
                                {/* Assistant Title */}
                                <div className="mb-6">
                                    <TitleField
                                        heading="Assistant Title"
                                        description="Give your custom assistant a unique name so it's easy to identify."
                                        value={title}
                                        onChange={handleTitleChange}
                                        onKeyDown={handleTitleKeyDown}
                                        formSubmitted={formSubmitted}
                                        disabled={isSubmitting}
                                        baseInputClass="mt-1 block w-full rounded-md shadow-sm p-3 sm-text-sm border"
                                        counterClassName="text-sm text-gray-500 text-right"
                                    />
                                </div>

                                {/* Set Assistant URL */}
                                <div className="mt-6 mb-6">
                                    <UrlField
                                        heading="Set Assistant URL"
                                        description="Choose a unique URL. Letters, numbers, and hyphens only."
                                        value={assistantUrl}
                                        onChange={handleAssistantUrlChange}
                                        formSubmitted={formSubmitted}
                                        isDuplicate={isAssistantUrlDuplicate}
                                        disabled={isSubmitting}
                                        baseInputClass="mt-1 block w-full rounded-md shadow-sm p-3 sm-text-sm border"
                                        fullUrlPreview={fullUrlPreview}
                                    />
                                </div>

                                {/* Prompt Message */}
                                <div className="mt-6 mb-6">
                                    <PromptField
                                        heading="Prompt Message"
                                        description="Direct the behavior of the assistant, including its tasks and how it completes them."
                                        value={promptMessage}
                                        onChange={handlePromptMessageChange}
                                        onKeyDown={handlePromptMessageKeyDown}
                                        formSubmitted={formSubmitted}
                                        disabled={isSubmitting}
                                        baseTextareaClass="mt-1 block w-full min-h-[150px] rounded-md shadow-sm p-3 sm-text-sm border"
                                        counterClassName="text-sm text-gray-500 text-right"
                                    />
                                </div>

                                {/* Welcome Message */}
                                <div className="mt-6 mb-6">
                                    <WelcomeField
                                        heading="Welcome Message"
                                        value={welcomeMessage}
                                        onChange={handleWelcomeMessageChange}
                                        onKeyDown={handleWelcomeMessageKeyDown}
                                        disabled={isSubmitting}
                                        textareaClassName="mt-1 block w-full min-h-[150px] rounded-md shadow-sm p-3 sm-text-sm border border-jtc-grey focus:border-jtc-blue focus:ring-1 focus:ring-jtc-blue focus:outline-none"
                                        counterClassName="text-sm text-gray-500 text-right"
                                    />
                                </div>

                                {/* Tools */}
                                <div className="mt-6 mb-6">
                                    <ToolsField
                                        heading="Tools"
                                        disabled={isSubmitting}
                                        enabledTools={enabledTools}
                                        onToggle={toggleTool}
                                        toolDefaults={toolDefaults}
                                        onToggleDefault={toggleDefaultTool}
                                    />
                                </div>

                                {/* Add Users */}
                                <div className="mt-6 mb-6">
                                    <UsersField
                                        heading="Add Users"
                                        disabled={isSubmitting}
                                        selectedEntities={selectedEntities}
                                        onSelectEntity={handleEntitySelect}
                                        onRemoveEntity={handleEntityRemove}
                                    />
                                </div>

                                {/* Add Managers */}
                                <div className="mt-6 mb-6">
                                    <UsersField
                                        heading="Add Managers"
                                        description="Managers have full control of the assistant: edit, files, enable/disable, delete, and manage this list."
                                        disabled={isSubmitting}
                                        selectedEntities={selectedManagerEntities}
                                        onSelectEntity={handleManagerSelect}
                                        onRemoveEntity={handleManagerRemove}
                                        lockedEntityIds={builderManagerGroupId ? [builderManagerGroupId] : []}
                                    />
                                </div>

                                {/* File Upload Area */}
                                <FileUploadArea
                                    uploadedFiles={uploadedFiles}
                                    isDragOver={isDragOver}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onFileChange={handleFileChange}
                                    onRemoveFile={handleRemoveFile}
                                    documentSizeLimit={documentSizeLimitMb}
                                    videoSizeLimitGb={videoSizeLimitGb}
                                    fileLimit={fileLimit}
                                    isDisabled={isSubmitting}
                                />
                            </div>
                        )}
                    </div>

                    {/* Footer Buttons */}
                    <div className="border-t border-gray-200 p-4 bg-white flex justify-end gap-4">
                        {!isSuccess && (
                            <button
                                disabled={isDisabled}
                                className={`jtc-primary-button transition flex items-center justify-center ${isSubmitting ? 'opacity-100 cursor-not-allowed' : 'bg-jtc-pink hover:bg-jtc-purple'
                                    }`}
                                onClick={handleSubmit}
                            >
                                {isSubmitting ? <ClipLoader size={16} color="white" /> : 'Create'}
                            </button>
                        )}
                        <button
                            className="jtc-secondary-button transition hover:bg-jtc-secondary-hover"
                            onClick={onCancel}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </fieldset>

            <ErrorDialog
                open={showErrorDialog}
                setOpen={setShowErrorDialog}
                errorMessage={errorMessage}
            />
        </div>
    );
};
